import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, Modal, Tab } from '@mui/material';
import Slide from '@mui/material/Slide';
import React, { useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { Descendant } from 'slate';
import { ReactComponent as GoogleGIcon } from '../../assets/google-g.svg';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import {
  type CardDetailsProps,
  getGoogleReviewsUrl,
  getPriceLevel,
  getStaticMapUrl,
  hasValidRating,
} from '../../utils/CardDetails';
import CardTags from '../Card/CardTags/CardTags';
import InlineRename from '../Card/InlineRename/InlineRename';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import StarRating from '../StarRating/StarRating';
import CardDetailsActionBar from './CardDetailsActionBar/CardDetailsActionBar';
import CardDetailsInfo from './CardDetailsInfo/CardDetailsInfo';
import CardImageGallery from './CardImageGallery/CardImageGallery';
import UserAddedSpan from './UserAddedSpan';
import { useCardDetailsModal } from './useCardDetailsModal';
import 'react-loading-skeleton/dist/skeleton.css';
import './CardDetailsModal.scss';

const GOOGLE_MAPS_ID = '5f2483670fd46e10';
const DESKTOP_FALLBACK_MAP_OPTIONS = {
  zoom: 15,
  size: { width: 600, height: 400 },
  scale: 2,
  mapId: GOOGLE_MAPS_ID,
} as const;

function CardDetailsModal({
  card,
  tripId,
  canEdit = false,
  open = false,
  listLabel,
  handleClose,
  handleDeleteClick,
}: CardDetailsProps) {
  const isMobile = useMobileMediaQuery();
  const cardTitleRef = useRef<HTMLHeadingElement>(null);

  const {
    notes,
    imgUrls,
    isLoadingImgUrls,
    isSlideVisible,
    isModalOpen,
    showToolbar,
    galleryOpen,
    isEditCoverPhoto,
    isRenamingCard,
    handleGalleryOpen,
    handleRename,
    handleRenameComplete,
    handleChangeCoverPhoto,
    handleGalleryClose,
    handleTabChange,
    handleCloseModal,
    submitCoverImageChange,
    debouncedSaveNotes,
    handleEditorClick,
    handleEditorBlur,
    tabValue,
    onImgError,
  } = useCardDetailsModal({ card, tripId, canEdit, open, isMobile, handleClose });

  // imgUrls holds only the cover until the gallery opens, so count photos from the card itself.
  const photoCount = (card.photoReferences ?? card.imgUrls)?.length ?? 0;
  const coverUrl = galleryOpen ? imgUrls[card.defaultImage || 0] : imgUrls[0];

  const validRating = hasValidRating(card.rating);
  const desktopFallbackMapUrl = getStaticMapUrl(card, DESKTOP_FALLBACK_MAP_OPTIONS);
  const containerClassName = ['card-details-modal-container', !canEdit && 'readonly']
    .filter(Boolean)
    .join(' ');
  const editorContainerClassName = `text-editor-container ${showToolbar ? 'with-toolbar' : 'without-toolbar'}`;

  const modalContent = (
    <div className={containerClassName}>
      <div className='header-container'>
        <Button
          className={isMobile ? 'back-button button' : undefined}
          sx={isMobile ? undefined : { minWidth: 0 }}
          startIcon={<ArrowBackIcon />}
          onClick={handleCloseModal}
        />
        <CardDetailsActionBar
          card={card}
          tripId={tripId}
          canEdit={canEdit}
          onRename={handleRename}
          onChangeCoverPhoto={handleChangeCoverPhoto}
          onDelete={handleDeleteClick}
        />
      </div>

      {imgUrls.length > 0 ? (
        <div className='main-cover-image-container'>
          <img
            className='cover-image'
            src={coverUrl}
            alt=''
            onError={onImgError}
          />
          {photoCount > 1 && (
            <button
              type='button'
              className='open-photo-gallery-button'
              onClick={handleGalleryOpen}
            >
              See all photos
            </button>
          )}
        </div>
      ) : isLoadingImgUrls ? (
        <div className='main-cover-image-container'>
          <Skeleton
            containerClassName='cover-image-skeleton-container'
            className='cover-image-skeleton'
          />
        </div>
      ) : !isMobile ? (
        <div className='static-map-container'>
          <img
            className='static-map'
            src={desktopFallbackMapUrl}
            alt=''
          />
        </div>
      ) : null}

      {galleryOpen && (
        <CardImageGallery
          imgUrls={imgUrls}
          isLoading={isLoadingImgUrls}
          placeholderCount={photoCount}
          isEdit={isEditCoverPhoto}
          galleryOpen={galleryOpen}
          handleGalleryClose={handleGalleryClose}
          originalSelectedImgIndex={card.defaultImage}
          submitCoverImageChange={submitCoverImageChange}
        />
      )}

      <div style={{ position: 'relative' }}>
        <CardTags
          card={card}
          isLarge={true}
          isModal={true}
          isMobileModal={isMobile}
        />
        <h1
          className={`card-title${!isMobile && canEdit ? ' editable' : ''}`}
          contentEditable={false}
          suppressContentEditableWarning={true}
          ref={cardTitleRef}
        >
          {card.customName ?? card.name}
        </h1>
        <InlineRename
          card={card}
          tripId={tripId}
          isActive={isRenamingCard}
          isLarge={true}
          isModal={true}
          onComplete={handleRenameComplete}
        />
      </div>

      <div className='subheader-container'>
        {card.placeType && <span className='card-place-type'>{card.placeType}</span>}
        {card.priceLevel !== undefined && Number.isFinite(card.priceLevel) && (
          <span className='price-level-text'>{getPriceLevel(card.priceLevel)}</span>
        )}
        {card.rating && (
          <div className='rating-with-count'>
            <span className='rating-value'>{card.rating.toFixed(1)}</span>
            <StarRating
              rating={card.rating}
              size={isMobile ? 'small' : 'medium'}
            />
            {card.userRatingsTotal !== undefined && (
              <span className='review-count'>({card.userRatingsTotal.toLocaleString()})</span>
            )}
          </div>
        )}
        {validRating && (
          <div className='google-reviews-link-container'>
            <a
              className='google-reviews-anchor'
              href={getGoogleReviewsUrl(card)}
              target='_blank'
              rel='noreferrer'
              aria-label='Google Reviews'
            >
              {isMobile ? (
                <GoogleGIcon
                  className='google-g-icon'
                  aria-hidden='true'
                />
              ) : (
                <>
                  <span className='google-reviews-text'>Google Reviews</span>
                  <LaunchIcon sx={{ fontSize: 20, m: 0, p: 0 }} />
                </>
              )}
            </a>
          </div>
        )}
      </div>

      <TabContext value={tabValue}>
        <TabList
          className='card-details-tab-list'
          onChange={handleTabChange}
        >
          {(notes || canEdit) && (
            <Tab
              className='styled-tab'
              label='Notes'
              value='1'
            />
          )}
          <Tab
            className='styled-tab'
            label='Info'
            value='2'
          />
        </TabList>

        {(notes || canEdit) && (
          <TabPanel
            className={`card-details-tab-panel${isMobile ? ' notes' : ''}`}
            value='1'
            onClick={handleEditorClick}
            onBlur={handleEditorBlur}
          >
            <div className={editorContainerClassName}>
              <RichTextEditor
                initialValue={notes}
                onChange={(newNotes: Descendant[]) => debouncedSaveNotes(newNotes)}
                toolbar={showToolbar}
                placeholder={canEdit ? 'Add notes/tips for this place...' : undefined}
                readOnly={!canEdit}
              />
            </div>
          </TabPanel>
        )}

        <TabPanel
          className={`card-details-tab-panel${isMobile ? ' info' : ''}`}
          value='2'
        >
          <CardDetailsInfo
            card={card}
            isMobile={isMobile}
          />
        </TabPanel>
      </TabContext>

      {!isMobile && (card.creator || listLabel) && (
        <div className='user-added-grid-container'>
          {listLabel && <span className='user-added-list-label'>{listLabel}</span>}
          {card.creator && <UserAddedSpan card={card} />}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Modal
        open={open}
        onClose={handleCloseModal}
      >
        {modalContent}
      </Modal>
    );
  }

  return (
    <Modal
      open={isModalOpen}
      onClose={handleCloseModal}
    >
      <Slide
        direction='left'
        in={isSlideVisible}
        mountOnEnter
        unmountOnExit
      >
        {modalContent}
      </Slide>
    </Modal>
  );
}

CardDetailsModal.displayName = 'CardDetailsModal';
export default CardDetailsModal;
